"use client";

import { useCallback, useMemo } from "react";

import Image from "next/image";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/flowbite/tooltip";
import { Filter2Icon } from "@/components/icons-lib";
import { DEFAULT_MAP_STYLE, MAP_STYLE_OPTIONS } from "@/providers/maps/constants";
import { useMapStyle } from "@/providers/maps/contexts/map-style-context";
import type { MapStyle } from "@/providers/maps/enums/map-style.enum";
import { useAppSelector } from "@/providers/store/hooks";
import { cn } from "@/shared/helpers/cn.helper";

interface MapStyleSelectorProps {
  districtId?: string | null;
  mobile?: boolean;
}

export function MapStyleSelector({
  districtId: districtIdProp,
  mobile = false,
}: MapStyleSelectorProps = {}) {
  const reduxDistrictId = useAppSelector((s) => s.district.selectedDistrict?.id ?? null);
  const districtId = districtIdProp ?? reduxDistrictId;
  const { mapStyle, setMapStyle } = useMapStyle();
  const currentStyle = useMemo(
    () => (districtId != null ? (mapStyle[districtId] ?? DEFAULT_MAP_STYLE) : DEFAULT_MAP_STYLE),
    [districtId, mapStyle],
  );

  const handleSelect = useCallback(
    (style: MapStyle, close: () => void) => {
      if (districtId != null) setMapStyle(districtId, style);
      close();
    },
    [districtId, setMapStyle],
  );

  return (
    <Popover>
      <div className="relative">
        <PopoverButton
          aria-label="Select map style"
          className={cn(
            "flex shrink-0 items-center justify-center border border-gray-100 bg-white text-gray-600 shadow-sm",
            mobile ? "h-[52px] w-[52px] rounded-full" : "h-[60px] w-[60px] rounded-lg",
          )}
        >
          <Filter2Icon className="h-6 w-6 text-gray-950" />
        </PopoverButton>

        <PopoverPanel
          transition
          className={cn(
            "data-closed:scale-95 data-closed:opacity-0 absolute z-10",
            "flex flex-row gap-2.5 rounded-xl border border-gray-200 bg-white px-2 py-1.5 shadow-sm",
            mobile
              ? "bottom-full left-0 mb-2.5"
              : "right-full top-1/2 mr-2.5 -translate-y-1/2",
          )}
        >
          {({ close }) => (
            <>
              {MAP_STYLE_OPTIONS.map(({ style, imageSrc, label }) => (
                <Tooltip key={style}>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        "flex h-11 w-11 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 transition-colors duration-300",
                        {
                          "border-darkRed-700": currentStyle === style,
                          "border-transparent": currentStyle !== style,
                        },
                      )}
                      type="button"
                      onClick={() => handleSelect(style, close)}
                    >
                      <div className="h-full w-full overflow-hidden rounded-lg p-0.5">
                        <Image
                          alt={label}
                          className="h-full w-full rounded-lg object-cover"
                          height={40}
                          src={imageSrc}
                          width={40}
                        />
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">{label}</TooltipContent>
                </Tooltip>
              ))}
            </>
          )}
        </PopoverPanel>
      </div>
    </Popover>
  );
}
